import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';

const TermsAndConditions: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Terms and Conditions
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            SafetyCheckNow Mobile Application
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last Updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mb: 4, backgroundColor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Agreement to Terms
            </Typography>
            <Typography variant="body1">
              By downloading, installing, or using the SafetyCheckNow mobile application ("App"), you agree to be bound by these Terms and Conditions ("Terms"). 
              If you do not agree to these Terms, do not download, install, or use the App. These Terms constitute a legally binding agreement between you and SafetyCheckNow.
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            These Terms and Conditions govern your use of the SafetyCheckNow mobile application and related services. 
            By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Legal Binding Agreement"
                secondary="These Terms create a legally binding agreement between you and SafetyCheckNow Ltd."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Age Requirement"
                secondary="You must be at least 13 years old to use this App, or have proper authorization from your parent/guardian and employer if under 18"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Authority to Accept"
                secondary="You represent that you have the legal authority to accept these Terms on behalf of yourself or your organization"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            2. Description of Service
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            SafetyCheckNow is a workplace safety compliance and asset management application designed to help organizations 
            maintain safety standards, track equipment, and ensure regulatory compliance.
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Core Features
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Safety Inspections"
                secondary="Digital safety check forms and equipment inspection workflows"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Asset Management"
                secondary="Asset tracking, condition monitoring, and maintenance scheduling"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Compliance Reporting"
                secondary="Automated compliance reports and audit trail generation"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Incident Management"
                secondary="Incident reporting and investigation workflow management"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Time Tracking"
                secondary="Work time recording with location verification for safety compliance"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            3. User Accounts and Registration
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Account Creation
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Employer Authorization"
                secondary="Account creation requires authorization from your employer or organization administrator"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Accurate Information"
                secondary="You must provide accurate, current, and complete information during registration"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Account Security"
                secondary="You are responsible for maintaining the security of your account credentials"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Account Responsibilities
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Authorized Use Only"
                secondary="Your account is for your personal use only and may not be shared with others"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Prompt Notification"
                secondary="You must notify us immediately of any unauthorized use of your account"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Compliance with Policies"
                secondary="You must comply with your organization's policies and procedures when using the App"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            4. Acceptable Use Policy
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#fff3cd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="warning.main">
                Important: Safety Compliance
              </Typography>
              <Typography variant="body1">
                This App is designed for legitimate workplace safety compliance purposes. 
                Misuse of safety features could result in serious safety risks and legal consequences.
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Permitted Uses
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Conducting legitimate safety inspections and equipment checks" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Recording accurate safety compliance data and incident reports" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Managing workplace assets in accordance with safety regulations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Communicating safety-related information with authorized personnel" />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Prohibited Uses
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Submitting false or misleading safety inspection data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Using the App for personal, non-work-related activities" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Attempting to circumvent security measures or access unauthorized data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Sharing account credentials or allowing unauthorized access" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Using the App in any way that could compromise workplace safety" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Interfering with the App's operation or security features" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            5. Location Services and Privacy
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            The App uses location services for legitimate safety compliance purposes as detailed in our Privacy Policy.
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Location Data Usage
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Safety Check Verification"
                secondary="Location data is used to verify where safety inspections are performed"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Asset Tracking"
                secondary="GPS coordinates help track equipment location for safety compliance"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Time Tracking Accuracy"
                secondary="Location verification ensures accurate work time recording"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Your Rights
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Location Control"
                secondary="You can control location permissions through your device settings"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Access"
                secondary="You can request access to your location data as outlined in our Privacy Policy"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Transparency"
                secondary="We will clearly indicate when and why location data is being collected"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            6. Data Ownership and Rights
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Your Data Rights
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Data Ownership"
                secondary="You retain ownership of the personal data you provide through the App"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Organization Data"
                secondary="Your employer may have rights to work-related data created through the App"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Portability"
                secondary="You have the right to request a copy of your personal data in a portable format"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            License Grant
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Limited License"
                secondary="We grant you a limited, non-exclusive, non-transferable license to use the App"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Authorized Use Only"
                secondary="The license is limited to authorized use in accordance with these Terms"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Termination"
                secondary="This license automatically terminates if you violate these Terms"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            7. Safety and Compliance Obligations
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#d4edda' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Critical Safety Notice
              </Typography>
              <Typography variant="body1">
                This App is a tool to assist with safety compliance but does not replace proper safety training, 
                procedures, or professional judgment. Always follow your organization's safety protocols and 
                applicable regulations.
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            User Obligations
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Accurate Reporting"
                secondary="You must provide accurate and truthful information in all safety reports and inspections"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Immediate Safety Concerns"
                secondary="Report immediate safety hazards through appropriate emergency channels, not just through the App"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Professional Judgment"
                secondary="Use your professional training and judgment alongside the App's guidance"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regulatory Compliance"
                secondary="Ensure your use of the App complies with all applicable safety regulations and standards"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            8. Limitation of Liability
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            To the maximum extent permitted by applicable law:
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Service Availability"
                secondary="We do not guarantee that the App will be available at all times or free from interruptions"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Accuracy"
                secondary="While we strive for accuracy, we cannot guarantee that all data in the App is error-free"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Safety Responsibility"
                secondary="Ultimate responsibility for workplace safety remains with you and your organization"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Third-Party Content"
                secondary="We are not responsible for third-party content, services, or integrations"
              />
            </ListItem>
          </List>

          <Card variant="outlined" sx={{ mt: 3, backgroundColor: '#f8d7da' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error.main">
                Important Limitation
              </Typography>
              <Typography variant="body1">
                In no event shall SafetyCheckNow be liable for any indirect, incidental, special, consequential, 
                or punitive damages, including but not limited to loss of profits, data, or business interruption, 
                arising out of or in connection with your use of the App.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            9. Termination
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Termination by You
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Account Deletion"
                secondary="You may request account deletion at any time, subject to data retention requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="App Removal"
                secondary="You may stop using the App by uninstalling it from your device"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Termination by Us
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Terms Violation"
                secondary="We may terminate your access immediately if you violate these Terms"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Safety Concerns"
                secondary="We may suspend access if we believe there are safety-related concerns"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Service Discontinuation"
                secondary="We may discontinue the service with reasonable notice"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Effect of Termination
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Access Cessation"
                secondary="Upon termination, your right to use the App immediately ceases"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Retention"
                secondary="We may retain certain data as required for compliance and legal obligations"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Survival of Terms"
                secondary="Certain provisions of these Terms will survive termination"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            10. Updates and Modifications
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We may update these Terms from time to time to reflect changes in our services, legal requirements, or business practices.
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Notification of Changes"
                secondary="We will notify you of material changes through the App or by email"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Continued Use"
                secondary="Your continued use of the App after changes constitutes acceptance of the updated Terms"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Review Responsibility"
                secondary="You are responsible for regularly reviewing these Terms"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            11. Governing Law and Dispute Resolution
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="Governing Law"
                secondary="These Terms are governed by and construed in accordance with the laws of England and Wales"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Jurisdiction"
                secondary="Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Alternative Dispute Resolution"
                secondary="We encourage resolution of disputes through direct communication before formal legal proceedings"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            12. Contact Information
          </Typography>
          
          <Card variant="outlined" sx={{ backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SafetyCheckNow Support Team
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about these Terms and Conditions, please contact us:
              </Typography>
              
              <Typography variant="body1" component="div">
                <strong>Email:</strong> support@safetychecknow.com<br />
                <strong>Legal:</strong> legal@safetychecknow.com
              </Typography>
              
              <Typography variant="body2" sx={{ mt: 2 }}>
                We are committed to addressing your questions and concerns promptly. 
                We will respond to all inquiries within 2 business days.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            These Terms and Conditions were last updated on {new Date().toLocaleDateString()} and are effective immediately.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            By using SafetyCheckNow, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please also review our <a href="/privacy-policy" style={{ color: '#2563eb', textDecoration: 'none' }}>Privacy Policy</a> to understand how we collect, use, and protect your information.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsAndConditions; 